import {UseQueryResult, useQuery} from "@tanstack/react-query";
import {api} from "@/features/api";
import {getTimestampWithCzechOffset} from "@/utils/date";

export type ThemeOptions =
  | "blue"
  | "green"
  | "gray"
  | "orange"
  | "purple"
  | "red"
  | "yellow";

export type ImageOptions =
  | "bag-present.svg"
  | "concept-test.svg"
  | "education.svg"
  | "eyeglass.svg"
  | "online-class.svg"
  | "online-shopping.svg"
  | "piggy-bank.svg"
  | "relax.svg"
  | "social-media.svg"
  | "speaker.svg"
  | "video.svg";

export type Poll = {
  slug: string;
  priority?: number;
  title?: string;
  image?: ImageOptions;
  credits?: number;
  theme?: ThemeOptions;
  locked?: boolean;
  lockedMessage?: string;
  hidden?: boolean;
  completed?: boolean;
  deadline?: number;
  externalLink?: string;
  targetingId?: number;
  type?: "classic" | "introset";
};

export const defaultTheme = "blue" as ThemeOptions;
export const defaultImage = "concept-test.svg" as ImageOptions;

type ResponseType = Poll[];

const initialData: ResponseType = [];

/**
 * Active polls are polls that are not hidden, not completed and have not passed their deadline.
 */
export const getActivePolls = (polls: Poll[]) => {
  // timestamp witch Czech time zone offset
  const timestampWithCzechOffset = getTimestampWithCzechOffset();

  return polls.filter((poll: Poll) => {
    return (
      !poll.hidden &&
      !poll.completed &&
      (!poll.deadline || poll.deadline > timestampWithCzechOffset)
    );
  });
};

const useGetUserPolls: (
  userId: string | boolean | undefined,
) => UseQueryResult<ResponseType, Error> = (userId) => {
  return useQuery<ResponseType, Error, ResponseType>({
    queryKey: ["getUserPolls", userId],

    // refetch data on window focus
    // this refetch is mandatory to avoid showing the poll after it has been completed in another tab or device
    refetchOnWindowFocus: true,

    queryFn: async () => {
      const response = await api("RTDB_API_URL").get(
        `/users/${userId}/polls.json`,
      );

      if (response.status === 401)
        throw new Error("Neoprávněný přístup k Firebase databázi.");

      if (!response.ok)
        throw new Error(
          `HTTP ${response.status} při čtení dat z Firebase databáze.`,
        );

      const json = await response.json();

      if (!json) return initialData;

      // convert object to array with slug as key
      const polls = Object.keys(json).map((key) => ({
        ...json[key],
        slug: key,
      }));

      try {
        const activePolls = getActivePolls(polls);

        // Set the app badge count to the number of polls.
        if (navigator.setAppBadge) navigator.setAppBadge(activePolls.length);
      } catch (error) {
        console.error("Failed to set app badge:", error);
      }

      return polls;
    },
    enabled: !!userId,
  });
};

export {useGetUserPolls};
